import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, ShieldCheck, ShieldAlert, Clock, Upload, CheckCircle2, XCircle, FileCheck2 } from "lucide-react";

const MAX_IMG = 4 * 1024 * 1024;

export default function Verify() {
  const { user, refreshUser } = useAuth();
  const t = useT();
  usePageMeta(t("verify.meta_title"));
  const [data, setData] = useState(null);
  const [dpaChecked, setDpaChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/coach/verify/status"); setData(d); }
    catch { setData({ verification: { status: "unverified" }, is_approved: false }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const v = data?.verification || {};
  const status = v.status || "unverified";
  const dpaAccepted = Boolean(v.dpa_accepted);
  const approved = data?.is_approved;
  const reviewer = data?.is_reviewer_bypass;

  const acceptDpa = async () => {
    setBusy(true);
    try {
      await api.post("/coach/verify/accept-dpa", { accepted: true, version: data?.dpa_required_version || "2026-07" });
      toast.success(t("verify.dpa_accepted"));
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_IMG) { toast.error(t("verify.too_large")); return; }
    setBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      const b64 = String(dataUrl).split(",")[1] || "";
      await api.post("/coach/verify/upload", { image_base64: b64, file_name: file.name, mime_type: file.type || "image/jpeg" });
      await load();
      await refreshUser();
    } catch (err) { toast.error(apiError(err, t("verify.upload_failed"))); }
    finally { setBusy(false); }
  };

  const STATUS = {
    approved: { label: t("verify.status_approved"), icon: ShieldCheck, cls: "bg-emerald-100 text-emerald-700" },
    pending_ai: { label: t("verify.status_pending_ai"), icon: Clock, cls: "bg-amber-100 text-amber-700" },
    pending_admin: { label: t("verify.status_pending_admin"), icon: Clock, cls: "bg-amber-100 text-amber-700" },
    rejected: { label: t("verify.status_rejected"), icon: XCircle, cls: "bg-rose-100 text-rose-700" },
    unverified: { label: t("verify.status_unverified"), icon: ShieldAlert, cls: "bg-secondary text-muted-foreground" },
  };
  const st = STATUS[approved ? "approved" : status] || STATUS.unverified;

  return (
    <div data-testid="coach-verify-page">
      <Seo title={t("verify.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="verify-back">
        <ChevronLeft className="h-4 w-4" /> {t("prof.back")}
      </Link>
      <PageHeader eyebrow={t("verify.eyebrow")} title={t("verify.title")} subtitle={t("verify.subtitle")} />

      {data === null ? (
        <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
      ) : (
        <div className="space-y-4">
          {/* Status badge */}
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-5" data-testid="verify-status">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${st.cls}`}>
              <st.icon className="h-4 w-4" /> {st.label}
            </span>
            <span className="text-sm text-muted-foreground">{t("verify.status")}</span>
          </div>

          {approved ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="verify-approved">
              <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-5 w-5" />
                <p className="font-display text-lg">{reviewer ? t("verify.reviewer_title") : t("verify.approved_title")}</p>
              </div>
              <p className="mt-1 text-sm text-emerald-800/80">{reviewer ? t("verify.reviewer_sub") : t("verify.approved_sub")}</p>
            </div>
          ) : (
            <>
              {/* Step 1 · DPA */}
              <div className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center gap-2">
                  {dpaAccepted ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">1</span>}
                  <p className="font-semibold text-foreground">{t("verify.step_dpa_title")}</p>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{t("verify.step_dpa_sub")}</p>
                {!dpaAccepted && (
                  <div className="mt-3 space-y-3">
                    <label className="flex items-start gap-2 text-sm text-foreground">
                      <input type="checkbox" checked={dpaChecked} onChange={(e) => setDpaChecked(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" data-testid="verify-dpa-check" />
                      {t("verify.dpa_accept")}
                    </label>
                    <Button className="rounded-full" disabled={!dpaChecked || busy} onClick={acceptDpa} data-testid="verify-dpa-accept">{t("verify.accept_btn")}</Button>
                  </div>
                )}
              </div>

              {/* Step 2 · Upload */}
              <div className={`rounded-2xl border border-border bg-card p-5 ${!dpaAccepted ? "opacity-60" : ""}`}>
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">2</span>
                  <p className="font-semibold text-foreground">{t("verify.step_upload_title")}</p>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{t("verify.step_upload_sub")}</p>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="verify-file-input" />
                <Button variant="outline" className="mt-3 gap-1.5 rounded-full" disabled={!dpaAccepted || busy} onClick={() => fileRef.current?.click()} data-testid="verify-upload">
                  {busy ? <Clock className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {busy ? t("verify.uploading") : (status === "rejected" || status === "pending_admin" ? t("verify.reupload_btn") : t("verify.upload_btn"))}
                </Button>
              </div>

              {/* Pending / Rejected / AI verdict */}
              {status === "pending_admin" && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="verify-pending-admin">
                  <p className="font-semibold text-amber-800">{t("verify.pending_admin_title")}</p>
                  <p className="mt-1 text-sm text-amber-800/80">{t("verify.pending_admin_sub")}</p>
                </div>
              )}
              {status === "rejected" && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5" data-testid="verify-rejected">
                  <p className="font-semibold text-rose-800">{t("verify.rejected_title")}</p>
                  <p className="mt-1 text-sm text-rose-800/80">{v.admin_verdict?.reason || t("verify.rejected_sub")}</p>
                </div>
              )}
              {v.ai_verdict && (status === "pending_admin" || status === "rejected") && (
                <div className="rounded-2xl border border-border bg-card p-5 text-sm" data-testid="verify-ai">
                  <p className="mb-2 flex items-center gap-1.5 font-semibold text-foreground"><FileCheck2 className="h-4 w-4 text-primary" /> {t("verify.ai_verdict")}</p>
                  <p className="text-muted-foreground">{t("verify.ai_subject")}: <span className="text-foreground">{v.ai_verdict.subject_area || "—"}</span></p>
                  <p className="text-muted-foreground">{t("verify.ai_confidence")}: <span className="text-foreground">{Math.round((v.ai_verdict.confidence || 0) * 100)}%</span></p>
                  {v.ai_verdict.notes && <p className="mt-1 text-muted-foreground">{v.ai_verdict.notes}</p>}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
